import { Button, Form, Input, InputNumber, Select } from 'antd';
import usersApiCall from 'apps/investment/apiCalls/user/oldList';
import DebounceSelect from 'common/ui/DebouceSelect';
import { useFetchApiList } from 'hooks/api';
import { useEffect } from 'react';
import { formItemLayout, tailFormItemLayout } from 'utils/formConfig';

const WealthProgramForm = ({
  form,
  submiting,
  onFinish,
  initialValues = {} as any,
  name,
}: any) => {
  const {
    data: users = [],
    load: fetchUserList,
    isLoading: isLoadingUsers,
  } = useFetchApiList(usersApiCall, { resourceName: 'data' });

  useEffect(() => {
    if (initialValues) {
      // Normalise start_date to plain YYYY-MM-DD string for the native date input
      const mapped = { ...initialValues };
      if (initialValues.start_date) {
        mapped.start_date = initialValues.start_date.slice(0, 10);
      }
      form.setFieldsValue(mapped);
    }
  }, [form, initialValues]);

  const isEdit = !!initialValues?.id;

  return (
    <Form
      {...formItemLayout}
      form={form}
      name={name || 'wealth_program_form'}
      onFinish={onFinish}
      scrollToFirstError
    >
      {/* User — only settable on create */}
      {!isEdit && (
        <Form.Item
          name="user_id"
          label="Customer"
          rules={[{ required: true, message: 'Please select a customer' }]}
        >
          <DebounceSelect
            placeholder="Search customer by name or email"
            fetchOptions={fetchUserList}
            fetching={isLoadingUsers}
            options={users}
            displayFn={(o: any) => ({
              value: o.id,
              label: `${o.name} (${o.email})`,
            })}
          />
        </Form.Item>
      )}

      <Form.Item
        name="start_date"
        label="Start Date"
        rules={[{ required: true, message: 'Please enter start date' }]}
      >
        {/* Native date input — no dayjs/moment dependency needed */}
        <Input type="date" style={{ width: '200px' }} />
      </Form.Item>

      <Form.Item name="status" label="Status" initialValue={1}>
        <Select style={{ width: '160px' }}>
          <Select.Option value={1}>Active</Select.Option>
          <Select.Option value={2}>Closed</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        name="inception_nav"
        label="Inception NAV"
        initialValue={10000}
        tooltip="Starting NAV per unit (default 10,000)"
      >
        <InputNumber
          style={{ width: '200px' }}
          min={1}
          step={100}
          formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        />
      </Form.Item>

      <Form.Item
        name="perf_fee_rate"
        label="Performance Fee Rate"
        initialValue={0.2}
        tooltip="Performance fee rate as decimal (e.g. 0.20 = 20%)"
      >
        <InputNumber
          style={{ width: '200px' }}
          min={0}
          max={0.5}
          step={0.01}
          formatter={v => `${(Number(v) * 100).toFixed(0)}%`}
          parser={v => Number(v?.replace('%', '')) / 100}
        />
      </Form.Item>

      <Form.Item
        name="perf_fee_hurdle_rate"
        label="Hurdle Rate"
        initialValue={0.05}
        tooltip="Annual hurdle rate for performance fee (e.g. 0.05 = 5%)"
      >
        <InputNumber
          style={{ width: '200px' }}
          min={0}
          max={0.3}
          step={0.005}
          formatter={v => `${(Number(v) * 100).toFixed(1)}%`}
          parser={v => Number(v?.replace('%', '')) / 100}
        />
      </Form.Item>

      <Form.Item {...tailFormItemLayout}>
        <Button type="primary" htmlType="submit" loading={submiting}>
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};

export default WealthProgramForm;
